import React, {createContext, ReactNode, useEffect, useState} from "react";
import {Alert, Button, Chip, ColorPaletteProp, IconButton, LinearProgress, Snackbar, Stack, Typography} from "@mui/joy";
import {
    CheckIcon,
    CircleAlert, ClipboardCheckIcon,
    InfoIcon,
    MessageCircleWarning,
    TriangleAlertIcon,
    UploadIcon, XCircleIcon,
    XIcon
} from "lucide-react";
import {useTranslation} from "react-i18next";
import {Notification} from "../models/notification";
import {Link} from "react-router-dom";
import {appRoute} from "../utils/routes";

export const IFeedbackContext = createContext<FeedbackContextState>({})

export type FeedbackContextState = {
    showFeedback?: (value: string, severity: ColorPaletteProp) => void,
    showResultsFeedback?: (notification:Notification) => void,
    showProgressFeedback?: (title: string, progress: number, cancelAction: () => void) => void
}

const FeedbackContext = ({children}) => {
    const [showAlert, setShowAlert] = useState(false)
    const [showProgressAlert, setShowProgressAlert] = useState(false)
    const [showResultsAlert, setShowResultsAlert] = useState(false)
    const [resultsNotification, setResultsNotification] = useState<Notification>()
    const [feedbackMessage, setFeedbackMessage] = useState("")
    const [progressTitle, setProgressTitle] = useState("")
    const [feedbackProgress, setFeedbackProgress] = useState(0)
    const [cancelAction, setCancelAction] = useState<() => void>()
    const [severity, setSeverity] = useState<ColorPaletteProp>()
    const {t} = useTranslation()
    const severityIconMapper: { [key in ColorPaletteProp]?: ReactNode } = {
        danger: <TriangleAlertIcon style={{color: "inherit"}}/>,
        success: <CheckIcon style={{color: "inherit"}}/>,
        warning: <CircleAlert style={{color: "inherit"}}/>,
        primary: <InfoIcon style={{color: "inherit"}}/>,
        neutral: <InfoIcon style={{color: "inherit"}}/>,
    }

    const handleClose = () => {
        setShowAlert(false)
        setShowProgressAlert(false)
        setShowResultsAlert(false)
    }

    const showFeedback = (message: string, severity: ColorPaletteProp) => {
        setFeedbackMessage(message)
        setSeverity(severity)
        setShowAlert(true)
    }

    const showProgressFeedback = (title: string, progress: number, cancelAction: () => void) => {
        setProgressTitle(title)
        setSeverity("secondary")
        setFeedbackProgress(progress)
        setShowProgressAlert(true)
        setCancelAction(cancelAction)

        if (progress === 100) {
            setTimeout(() => {
                setShowProgressAlert(false)
            }, 1000)
            setCancelAction(null)
        }
    }

    const showResultsFeedback = (notification: Notification) => {
        setShowResultsAlert(true)
        setResultsNotification(notification)
    }

    const state: FeedbackContextState = {
        showFeedback,
        showProgressFeedback,
        showResultsFeedback
    }

    const ResultFeedbackComponent = (props?: { notification: Notification }) => {
        return <Alert component={null} variant={"outlined"} size={"lg"}
                      sx={{width: "100%", backgroundColor: "background.level1"}} color={"secondary"}>
            <Stack gap={1}>
                <Stack gap={0.5}>
                    <Typography startDecorator={<InfoIcon/>} level={"title-lg"}>
                        {props.notification?.title}
                    </Typography>
                    <Typography level={"body-sm"}>
                        {props.notification?.body}
                    </Typography>
                </Stack>

                <Stack gap={1} direction={"row"} alignSelf={"flex-end"}>
                    {/* Targets are app paths ("/results/<id>"); this provider sits outside the router, so resolve them by hand. */}
                    <Button component={"a"} href={appRoute(props.notification?.action?.[0]?.target ?? "notifications")}
                            variant={"plain"} size={"sm"}
                            sx={{borderRadius: 10}}>
                        {t("resultFeedbackPositiveActionLabel")}
                    </Button>

                    <Button size={"sm"} variant={"plain"} color={"neutral"} onClick={() => setShowResultsAlert(false)}>
                        {t("resultFeedbackNegativeActionLabel")}
                    </Button>
                </Stack>

            </Stack>

        </Alert>
    }

    return <IFeedbackContext.Provider value={state}>
        {<Snackbar size={"lg"} onClose={handleClose} sx={{padding: 0, width: showProgressAlert ? "30rem" : "initial"}}
                   open={showAlert || showProgressAlert || showResultsAlert}
                   autoHideDuration={showProgressAlert ? null : showResultsAlert ? 4500 : severity === "danger" ? 3500 : 2500}
                   anchorOrigin={{
                       vertical: showProgressAlert ? "bottom" : "top",
                       horizontal: showResultsAlert ? "right" : "center"
                   }}>
            {showResultsAlert ?
                <ResultFeedbackComponent notification={resultsNotification}/> :
                <Alert component={null} size={"lg"} sx={{width: "100%"}}
                       color={showProgressAlert ? "neutral" : severity}
                       startDecorator={showProgressAlert ? null : severityIconMapper[severity]}>
                    {showProgressAlert ? <Stack gap={1.5} width={"100%"}>
                            <Stack justifyContent={"space-between"} direction={"row"} alignItems={"center"}>
                                <Typography level={"title-md"} startDecorator={<UploadIcon/>} textAlign={"center"}>
                                    {progressTitle}
                                </Typography>
                                <Chip size={"md"} variant={"soft"} color={"danger"} startDecorator={<XCircleIcon/>}
                                      sx={{alignSelf: "flex-end"}} onClick={() => cancelAction()}>
                                    {t("cancelDialogActionButtonLabel")}
                                </Chip>
                            </Stack>

                            <LinearProgress variant={"outlined"} determinate value={feedbackProgress}/>
                        </Stack> :
                        t(feedbackMessage)}
                </Alert>}
        </Snackbar>}
        {children}
    </IFeedbackContext.Provider>
}

export default FeedbackContext